import React, { useEffect, useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { 
  TextField, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Modal, 
  Box, 
  IconButton, 
  Tooltip 
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import useAuth from '../../../../features/hooks/useAuth';
import { useSelector } from 'react-redux';
import { CommonButton } from '../../../../Styles/CommonStyles';
import { useGetUsersQuery } from '../../../../features/users/usersApiSlice';
import { useGetofficesQuery } from '../../../../features/offices/officesApiSlice';
import { useGetStoreOfficesQuery } from '../../../../features/storeoffices/storeofficesApiSlice';
import { useUpdateConsumableReqMutation } from '../../../../features/consumablerequest/consumableReqApiSlice';
import { useGetconsumfinalQuery, useUpdateconsumfinalMutation } from '../../../../features/consumfinal/consumfinalApiSlice';
import { useGetConsumTransactionsQuery, useAddNewConsumTransactionMutation } from '../../../../features/consumtransactions/consumTransApiSlice';
import nameStringFn from '../../../../data/nameStringFn';
import UnivConsumSelector from '../UnivConsumSelector';
import { useUpdateOfficeItemMutation } from '../../../../features/officeitems/officeItemsApiSlice';

// Styled components
const Button = styled(CommonButton)`
  padding: 8px 16px;
  min-width: 120px;
  margin-right: 10px;
`;

const StyledButton = styled(CommonButton)`
  padding: 8px 16px;
  min-width: 120px;
  margin-right: 10px;
`;

const AcceptButton = styled(Button)`
  background-color: #4caf50;
`;

const ReSubmitButton = styled(Button)`
  background-color: #4caf50;
`;

const RejectButton = styled(Button)`
  background-color: #f44336;
`;

const RemarkContainer = styled.div`
  margin-top: 15px;
  width: 100%;
`;

const StyledTableContainer = styled(TableContainer)`
  margin-bottom: 20px;
  margin-top: 20px;
`;

const StyledTableHead = styled(TableHead)`
  background-color: #1976d2;
  
  & th {
    color: white;
    font-weight: bold;
  }
`;

const SelectedAssetsContainer = styled.div`
  margin-top: 20px;
  width: 100%;
`;

const SelectedAssetsTitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const SelectedAssetsTitle = styled.h3`
  margin-bottom: 0;
  color: #1976d2;
`;

const SummaryContainer = styled.div`
  margin-bottom: 15px;
  padding: 10px;
  background-color: #f5f5f5;
  border-radius: 5px;
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
`;

const SummaryItem = styled.div`
  display: flex;
  align-items: center;
  background-color: white;
  padding: 5px 10px;
  border-radius: 5px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
`;

const SummaryItemName = styled.span`
  font-weight: 500;
  margin-right: 10px;
`;

const CancelButton = styled(StyledButton)`
  background-color: #9e9e9e;
`;

const ConfirmButton = styled(StyledButton)`
  background-color: #4caf50;
`;

const ActionButton = styled(StyledButton)`
  background-color: #1976d2;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 10px;
`;

const ClearButton = styled(Button)`
  margin-right: auto;
  background-color: #f44336;
  color: white;
  &:hover {
    background-color: #d32f2f;
  }
`;

const SummaryItemCount = styled.span`
  background-color: #1976d2;
  color: white;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.8rem;
`;

const NotificationMessage = styled.div`
  margin-top: 10px;
  padding: 8px 16px;
  border-radius: 4px;
  font-weight: 500;
  display: flex;
  align-items: center;
  background-color: ${props => props.isPartial ? '#ff9800' : '#4caf50'};
  color: white;
  margin-bottom: 20px;
`;

// Error handling components
const ErrorMessage = styled.div`
  background-color: #f44336;
  color: white;
  padding: 10px 15px;
  border-radius: 4px;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ErrorCloseButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  font-size: 16px;
  cursor: pointer;
  padding: 0 5px;
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const LoadingText = styled.div`
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  font-weight: bold;
`;

const ModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  maxHeight: '80vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: '8px',
  overflow: 'auto'
};

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  gap: 10px;
`;

const ModalTitle = styled.h2`
  margin-bottom: 20px;
  color: #1976d2;
`;

const BoInboxFormBTS = ({ row, handleBack }) => {
  // State management with React hooks
  const [remark, setRemark] = useState('');
  const [openIssueModal, setOpenIssueModal] = useState(false);
  const [addSpecIndex, setAddSpecIndex] = useState(1);
  const [selectedConsum, setSelectedConsum] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Auth and office data from context/store
  const auth = useAuth();
  const user_current_office = useSelector((state) => state.office.off);
  
  // API queries with optimized selectFromResult to reduce rerenders
  const { getSingleuser } = useGetUsersQuery('userslist', {
    selectFromResult: ({ data }) => ({
      getSingleuser: data?.entities
    }),
  });

  const { officedata } = useGetofficesQuery('offices', {
    selectFromResult: ({ data }) => ({
      officedata: data?.entities
    }),
  });

  const { storeoffices } = useGetStoreOfficesQuery('offices', {
    selectFromResult: ({ data }) => ({
      storeoffices: data?.entities
    }),
  });

  // // Get office consumables data with refetch strategy
  // const { data: officeConsumables } = useGetOfficeConsumablesQuery(undefined, {
  //   refetchOnMountOrArgChange: true
  // });

  // Memoize IDs for workflow to prevent recreating on every render
  const unitHead_Id = useMemo(() => 
    officedata ? Object.values(officedata)?.find(office => office.name === user_current_office)?.unitHead : "",
    [officedata, user_current_office]
  );
  
  const L1ReqApprover_Id = useMemo(() =>
    storeoffices ? Object.values(storeoffices)?.find(office => office.storeoffice === user_current_office)?.roleUsers?.L1ReqApprover_C : "",
    [storeoffices, user_current_office]
  );
  
  // Mutations with better naming
  const [updateConsumableReq, { isLoading: isUpdatingReq }] = useUpdateConsumableReqMutation();
  const [addNewConsumTransaction, { isLoading: isAddingTransaction }] = useAddNewConsumTransactionMutation();
  const [updateconsumfinal, { isLoading: isUpdatingConsumFinal }] = useUpdateconsumfinalMutation();
  const [updateofficeitems] = useUpdateOfficeItemMutation();
  // Memoized sender object for API calls
  const senderObject = useMemo(() => ({
    id: auth?.id, 
    name: getSingleuser?.[auth?.id]?.name, 
    office: user_current_office, 
    designation: auth?.designation?.[user_current_office] 
  }), [auth?.id, auth?.designation, getSingleuser, user_current_office]);

  // Memoized creator receiver object
  const creatorReceiverObject = useMemo(() => ({
    id: row?.req_creator_id, 
    name: getSingleuser?.[row?.req_creator_id]?.name, 
    office: row?.req_data[0]?.sent_by?.office, 
    designation: row?.req_data[0]?.sent_by?.designation 
  }), [getSingleuser, row?.req_creator_id, row?.req_data]);

  // Memoized creator string for display
  const creatorReceiverString = useMemo(() => 
    `${nameStringFn(getSingleuser?.[row?.req_creator_id])}-${row?.req_data[0]?.sent_by?.office}-${getSingleuser?.[row?.req_creator_id]?.designation?.[row?.req_data[0]?.sent_by?.office] || 'Unknown'}`,
    [getSingleuser, row?.req_creator_id, row?.req_data]
  );

  // Handle remark changes with useCallback
  const handleRemarkChange = useCallback((e) => {
    setRemark(e.target.value);
  }, []);

  // Get the latest store consumable issues - memoized to prevent recalculation
  const getLatestStoreConsumIssue = useCallback(() => {
    if (row?.store_consum_issue && row.store_consum_issue.length > 0) {
      const latestIssue = row.store_consum_issue[row.store_consum_issue.length - 1];
      return latestIssue.data || [];
    }
    return [];
  }, [row?.store_consum_issue]);
  
  // Memoize latestConsumables to prevent recreation
  const latestConsumables = useMemo(() => getLatestStoreConsumIssue(), [getLatestStoreConsumIssue]);
  
  // Get the requested quantity for an item from the last consumables_requested object
  const getRequestedQuantity = useCallback((item) => {
    if (!row?.consumables_requested || row.consumables_requested.length === 0) {
      return item.quantity || 0;
    }
    
    const latestRequest = row.consumables_requested[row.consumables_requested.length - 1];
    if (!latestRequest?.data) {
      return item.quantity || 0;
    }
    
    // Find matching item in the latest requested data
    // Compare by consumable, asset, and assettag (not partno, as partno is assigned during issue creation)
    const requestedItem = latestRequest.data.find(reqItem => 
      reqItem.consumable === item.consumable && 
      reqItem.asset === item.asset &&
      (reqItem.assettag?.label === item.assettag?.label)
    );
    
    return requestedItem?.quantity || item.quantity || 0;
  }, [row?.consumables_requested]);

  // Initialize selectedConsum from row data only when row changes
  useEffect(() => {
    if (row?.consumables_requested && row?.consumables_requested?.length > 0) {
      const latestIssue = row.consumables_requested[row.consumables_requested.length - 1];
      if (latestIssue?.data) {
        setSelectedConsum(latestIssue.data);
        setAddSpecIndex(latestIssue.data.length);
      }
    }
  }, [row]);
  
  // Calculate if this is a partial issue by comparing original request with issued items
  const calculateIsPartialIssue = useCallback(() => {
    if (!row?.consumables_requested || 
        row.consumables_requested.length === 0 || 
        !latestConsumables.length) {
      return row?.store_partial_issue || false;
    }
    
    const latestRequest = row.consumables_requested[row.consumables_requested.length - 1];
    if (!latestRequest?.data) {
      return row?.store_partial_issue || false;
    }
    
    const requestedItems = latestRequest.data;
    const issuedItems = latestConsumables;
    
    // Check if partial issue based on group totals (to handle splits correctly)
    const issuedGroupTotals = {};
    const requestedGroupTotals = {};
    
    // Calculate totals for issued items by group
    issuedItems.forEach(item => {
      const groupId = item.groupId || 'unknown';
      if (!issuedGroupTotals[groupId]) {
        issuedGroupTotals[groupId] = 0;
      }
      issuedGroupTotals[groupId] += parseInt(item.selectedQuantity || item.quantity, 10);
    });
    
    // Calculate totals for requested items by group
    requestedItems.forEach((item, index) => {
      const groupId = `group-${index}`;
      requestedGroupTotals[groupId] = parseInt(item.quantity, 10);
    });
    
    // Check if any group has less total quantity than requested
    const isPartial = Object.keys(requestedGroupTotals).some(groupId => {
      const issuedTotal = issuedGroupTotals[groupId] || 0;
      const requestedTotal = requestedGroupTotals[groupId];
      return issuedTotal < requestedTotal;
    });
    
    return isPartial;
    
  }, [latestConsumables, row?.consumables_requested, row?.store_partial_issue]);
  
  // Memoize isPartialIssue to prevent recalculation
  const isPartialIssue = useMemo(() => calculateIsPartialIssue(), [calculateIsPartialIssue]);

  // Validate consumable quantity
  const hasValidQuantity = useCallback((data) => {
    if (!Array.isArray(data) || data.length === 0) {
      return false;
    }
  
    return data.every(item => {
      const quantity = parseInt(item.quantity, 10);
      return !isNaN(quantity) && quantity > 0;
    });
  }, []);
  
  // Generate summary data from consumables - memoize to prevent recalculation
  const generateSummary = useCallback((items) => {
    if (!items || items.length === 0) return [];

    return Object.entries(
      items.reduce((acc, item) => {
        const { consumable } = item;
        acc[consumable] = (acc[consumable] || 0) + parseInt(item.quantity, 10);
        return acc;
      }, {})
    ).map(([name, count]) => ({ name, count }));
  }, []);

  // Memoize summary data for each dataset to prevent recalculation
  const latestConsumablesSummary = useMemo(() => 
    generateSummary(latestConsumables),
    [generateSummary, latestConsumables]
  );

  const selectedConsumSummary = useMemo(() => 
    generateSummary(selectedConsum),
    [generateSummary, selectedConsum]
  );  

  // Handle modal actions with useCallback
  const handleReopenForEdit = useCallback(() => {
    setOpenIssueModal(true);
  }, []);

  const handleCloseIssueModal = useCallback(() => {
    if(!hasValidQuantity(selectedConsum)){
      alert("Please ensure all selected items have a valid quantity greater than 0.");
      return;
    }
    setOpenIssueModal(false);
  }, [hasValidQuantity, selectedConsum]);

  const handleClearAllSelections = useCallback(() => {
    setSelectedConsum([]);
    setAddSpecIndex(1);
    alert("All items have been deselected.");
  }, []);

  const handleConfirmSelection = useCallback(() => {
    if(!hasValidQuantity(selectedConsum)){
      alert("Please ensure all selected items have a valid quantity greater than 0.");
      return;
    }
    setOpenIssueModal(false);
  }, [hasValidQuantity, selectedConsum]);

  // Normalize data for comparison
  const normalizeForComparison = useCallback((item) => ({
    consumable: item.consumable,
    partno: item.partno,
    quantity: parseInt(item.quantity, 10),
    asset: item.asset || 'Not Applicable',
    assettagLabel: item.assettag?.label || 'Not Applicable'
  }), []);

  // Compare consumable arrays
  const areConsumablesEqual = useCallback((currentItems, previousItems) => {
    if (!Array.isArray(currentItems) || !Array.isArray(previousItems)) {
      return false;
    }
    
    // Quick check for array length difference
    if (currentItems.length !== previousItems.length) {
      return false;
    }
    
    // Normalize both arrays for comparison
    const normalizedCurrent = currentItems.map(normalizeForComparison);
    const normalizedPrevious = previousItems.map(normalizeForComparison);
    
    // Check if each item in current exists in previous with same properties
    return normalizedCurrent.every(currentItem => 
      normalizedPrevious.some(prevItem => 
        prevItem.consumable === currentItem.consumable &&
        prevItem.partno === currentItem.partno &&
        prevItem.quantity === currentItem.quantity &&
        prevItem.asset === currentItem.asset &&
        prevItem.assettagLabel === currentItem.assettagLabel
      )
    );
  }, [normalizeForComparison]);
console.log(selectedConsum)
  // Accept consumables and close request
  const handleAcceptConsumables = async () => {
    if (!remark.trim()) {
      alert("Please provide a remark before accepting the consumables.");
      return;
    }
    
    // Check if this is a partial issue by calculating at the time of acceptance
    const isPartialAtAcceptance = calculateIsPartialIssue();
    
    // Get the latest store consumable issue data
    const latestIssueData = getLatestStoreConsumIssue();
    if (!latestIssueData.length) {
      alert("No consumable data found to accept.");
      return;
    }

    try {
      setIsLoading(true);
      
      // Create transaction object
      let conTransObj = {
        transac_id: `StoreToBo-${Date.now().toString(36)}-${Math.random().toString(36).substr(2, 5)}`,
        req_id: row?._id || "",
        consumable:  "",
        partnumber:  "",
        quantity:  "",
        asset_id:  "",
        transac_type: "StoreToBo",
        from_userid: getSingleuser?.[L1ReqApprover_Id]?.email,
        from_office: row?.req_store,
        from_desig: getSingleuser?.[L1ReqApprover_Id]?.designation?.[row?.req_store],
        from_store: row?.req_store,
        to_userid: getSingleuser?.[row?.req_creator_id]?.email,
        to_office: row?.req_data[0]?.sent_by?.office,
        to_desig: row?.req_data[0]?.sent_by?.designation,
        to_store: row?.req_store,
        time_stamp: new Date().toISOString()
      };

      let senttoData = {
        sent_to_tag: "bo_bts_accept_close",
        Text: remark || "",
        action: `${isPartialAtAcceptance ? 'Partial' : 'Full'} consumables accepted and request closed`,      
        sent_by: { id: auth?.id, name: getSingleuser?.[auth?.id]?.name, office: user_current_office, designation: auth?.designation?.[user_current_office] },
        sent_to: { id: auth?.id, name: getSingleuser?.[auth?.id]?.name, office: user_current_office, designation: auth?.designation?.[user_current_office] },
        store_partial_issue: isPartialAtAcceptance,
        curr_sent_to_string: `${nameStringFn(getSingleuser?.[auth?.id])}-${user_current_office}-${auth?.designation?.[user_current_office] || 'Unknown'}`,
        time_stamp: new Date().toISOString(),
        curr_status: "Closed",
      };

      const body = { id: row?._id, senttoData: senttoData };

      // Update request status
      const result = await updateConsumableReq(body).unwrap();
      console.log("Request status updated successfully:", result);
      
      // Process each consumable item sequentially to avoid race conditions
      const itemsToProcess = row?.store_consum_issue[row?.store_consum_issue?.length - 1]?.data || [];
      const processedItems = [];
      const failedItems = [];
      
      for (let i = 0; i < itemsToProcess.length; i++) {
        const item = itemsToProcess[i];
        // Use selectedQuantity for split items, fallback to quantity
        const itemQuantity = item.selectedQuantity || item.quantity;
        
        console.log(`Processing item ${i + 1}/${itemsToProcess.length}:`, {
          consumable: item.consumable,
          partno: item.partno,
          quantity: itemQuantity
        });
        
        // Create a unique transaction object for each item
        const itemTransObj = {
          ...conTransObj,
          transac_id: `StoreToBo-${Date.now().toString(36)}-${Math.random().toString(36).substr(2, 5)}`,
          consumable: item.consumable,
          partnumber: item.partno,
          quantity: itemQuantity,
          asset_id: item.assettag ? item.assettag.value.oem_tag : ""
        };
        
        try {
          // Update inventory - decrement in-transit
          await updateconsumfinal({
            consumable: item.consumable, 
            partno: item.partno, 
            store: row.req_store, 
            isInTransit: -parseInt(itemQuantity, 10)
          }).unwrap();
          
          // Update office items
          await updateofficeitems({
            office_id: Object.values(officedata).find(x => x.name === user_current_office).id,        
            office_name: user_current_office,
            consumable_details: [          
              {
                partno: item.partno,
                consumable: item.consumable,
                quantity: itemQuantity                
              }
            ]
          });
          
          // Add transaction record
          await addNewConsumTransaction(itemTransObj);
          
          processedItems.push(item.consumable + ' - ' + item.partno);
          console.log(`Successfully processed: ${item.consumable} (${item.partno})`);
          
        } catch (error) {
          console.error(`Error processing item ${item.consumable} (${item.partno}):`, error);
          failedItems.push({
            item: item.consumable + ' - ' + item.partno,
            error: error.message
          });
        }
      }
      
      if (failedItems.length > 0) {
        console.error('Failed items:', failedItems);
        const failedList = failedItems.map(f => `${f.item}: ${f.error}`).join('\n');
        throw new Error(`Failed to process ${failedItems.length} item(s):\n${failedList}`);
      }
      
      console.log('All items processed successfully:', processedItems);
      
      alert(`${isPartialAtAcceptance ? 'Partial' : 'Full'} consumables accepted successfully. Request has been closed.`);
      handleBack();
    } catch (err) {
      setError(`Failed to accept consumables: ${err.message || 'Unknown error'}`);
      console.error("Error accepting consumables:", err);
      alert(`There was an error accepting the consumables: ${err.message || 'Unknown error'}. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle resubmitting to Unit Head
  const handleResubmitUnitHead = useCallback(async () => {
    if (!remark.trim()) {
      alert("Please provide a remark before resubmitting.");
      return;
    }
    
    if (!unitHead_Id) {
      alert("Unit Head information not found. Cannot resubmit.");
      return;
    }
    
    if (!hasValidQuantity(selectedConsum)) {
      alert("Please ensure all selected items have valid quantities greater than 0.");
      return;
    }
    
    try {
      // Determine if this is a return from Unit Head
      const isReturnedFromUnitHead = row?.curr_sent_to_tag === "boreq_bts_rtrned_uhlvl";
      
      // Check if the consumables selection has changed 
      let originalConsumables = [];
      if (row?.consumables_requested && row.consumables_requested.length > 0) {
        const latestRequest = row.consumables_requested[row.consumables_requested.length - 1];
        originalConsumables = latestRequest.data || [];
      }
      
      const selectionUnchanged = areConsumablesEqual(selectedConsum, originalConsumables);
      
      // Prepare request data
      const requestData = {
        sent_to_tag: "boreq_bts_resubmit_uh",
        Text: remark,
        sent_by: senderObject,
        sent_to: {
          id: unitHead_Id,
          name: getSingleuser?.[unitHead_Id]?.name,
          office: user_current_office,
          designation: getSingleuser?.[unitHead_Id]?.designation?.[user_current_office]
        },
        action: isReturnedFromUnitHead 
          ? "Request resubmitted to Unit Head after modification" 
          : "Consumable Request created",
        curr_sent_to_string: `${nameStringFn(getSingleuser?.[unitHead_Id])}-${user_current_office}-${getSingleuser?.[unitHead_Id]?.designation?.[user_current_office] || 'Unknown'}`,
        time_stamp: new Date()
      };
      
      // If consumables were changed, update the request with the new selection
      if (!selectionUnchanged || !row.consumables_requested) {
        requestData.consumables_requested = [{
          time_stamp: new Date(),
          actionNo: (row?.consumables_requested?.length || 0) + 1,
          data: selectedConsum.map(item => ({
            ...item,
            asset: item?.asset ? item.asset.trim() : "Not Applicable",
            assettag: item?.assettag ? item.assettag : {label: "Not Applicable", value: "Not Applicable"},
          }))
        }];
      }
      
      const body = { id: row?._id, senttoData: requestData };
      await updateConsumableReq(body).unwrap();
      
      let message;
      if (isReturnedFromUnitHead && selectionUnchanged) {
        message = "Request resubmitted to Unit Head without any changes to consumables. Only remarks were updated.";
      } else if (!selectionUnchanged) {
        message = "Request resubmitted to Unit Head with updated consumables.";
      } else {
        message = "Request resubmitted to Unit Head successfully.";
      }
      
      alert(message);
      handleBack();
    } catch (error) {
      console.error("Error resubmitting request:", error);
      alert(`Error: ${error.message || "There was an error resubmitting the request. Please try again."}`);
    }
  }, [
    remark, 
    unitHead_Id, 
    hasValidQuantity, 
    selectedConsum, 
    row?.curr_sent_to_tag,
    row?.consumables_requested,
    row?._id,
    areConsumablesEqual,
    senderObject,
    getSingleuser,
    user_current_office,
    updateConsumableReq,
    handleBack
  ]);

  // Reject the request
  const handleRejectRequest = useCallback(async () => {
    if (!remark.trim()) {
      alert("Please provide a reason for rejecting the request.");
      return;
    }

    try {
      const senttoData = {
        sent_to_tag: "bo_bts_reject",
        Text: remark,
        sent_by: senderObject,
        sent_to: creatorReceiverObject,
        action: "Request rejected by BO",
        curr_status: "Rejected",
        curr_sent_to_string: creatorReceiverString,
        time_stamp: new Date()
      };
      
      const body = { id: row?._id, senttoData };
      await updateConsumableReq(body).unwrap();
      
      alert("Request rejected successfully.");
      handleBack();
    } catch (error) {
      console.error("Error rejecting request:", error);
      alert(`Error: ${error.message || "There was an error rejecting the request. Please try again."}`);
    }
  }, [
    remark, 
    senderObject, 
    creatorReceiverObject, 
    creatorReceiverString, 
    row?._id, 
    updateConsumableReq, 
    handleBack
  ]);

  return (
    <>
      {error && (
        <ErrorMessage>
          {error}
          <ErrorCloseButton onClick={() => setError('')}>×</ErrorCloseButton>
        </ErrorMessage>
      )}
      
      {isLoading && (
        <LoadingOverlay>
          <LoadingText>Processing request...</LoadingText>
        </LoadingOverlay>
      )}
      
      {/* Remarks field */}
      <RemarkContainer>
        <TextField
          fullWidth
          label="Remarks"
          variant="outlined"
          multiline
          rows={4}
          value={remark}
          onChange={handleRemarkChange}
          placeholder="Add your remarks here..."
        />
      </RemarkContainer>

      {/* When request is from Store Level 2, show the issued consumables */}
      {row?.curr_sent_to_tag === "storel2_bts_forward_bo" && latestConsumables.length > 0 ? (
        <SelectedAssetsContainer>
          <SelectedAssetsTitleContainer>
            <SelectedAssetsTitle>Consumables Issued by Store</SelectedAssetsTitle>
          </SelectedAssetsTitleContainer>

          {/* Summary section */}
          <SummaryContainer>
            <span style={{ fontWeight: 'bold', marginRight: '10px' }}>
              Total: {latestConsumables.reduce((sum, item) => sum + parseInt(item.selectedQuantity || item.quantity, 10), 0)} items
            </span>
            {latestConsumablesSummary.map((item, index) => (
              <SummaryItem key={index}>
                <SummaryItemName>{item.name}</SummaryItemName>
                <SummaryItemCount>{item.count}</SummaryItemCount>
              </SummaryItem>
            ))}
          </SummaryContainer>
          
          <StyledTableContainer component={Paper}>
            <Table>
              <StyledTableHead>
                <TableRow>
                  <TableCell>Sl.No.</TableCell>
                  <TableCell>Consumable</TableCell>
                  <TableCell>Asset</TableCell>
                  <TableCell>Asset Tag</TableCell>
                  <TableCell>Part No</TableCell>
                  <TableCell>Requested Qty</TableCell>
                  <TableCell>Issued Qty</TableCell>
                </TableRow>
              </StyledTableHead>
              <TableBody>
                {latestConsumables.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{item.consumable || '-'}</TableCell>
                    <TableCell>{item.asset || 'Not Applicable'}</TableCell>
                    <TableCell>{item.assettag.label || 'Not Applicable'}</TableCell>
                    <TableCell>{item.partno || '-'}</TableCell>
                    <TableCell>{getRequestedQuantity(item) || '-'}</TableCell>
                    <TableCell>{item.selectedQuantity || item.quantity || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </StyledTableContainer>
          
          {/* Notification message */}
          <NotificationMessage isPartial={isPartialIssue}>
            {isPartialIssue 
              ? 'Partial items selected for issue. Some requested items or quantities will not be issued.' 
              : 'All requested items selected for issue with full quantities.'}
          </NotificationMessage>
        </SelectedAssetsContainer>
      ) : (
        <SelectedAssetsContainer>
          <SelectedAssetsTitleContainer>
            <SelectedAssetsTitle>Consumables Requested for Issue</SelectedAssetsTitle>
            {row?.curr_sent_to_tag === "boreq_bts_rtrned_uhlvl" && (
              <ActionButtonsContainer>
                <Tooltip title="Edit selections">
                  <IconButton color="primary" onClick={handleReopenForEdit}>
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              </ActionButtonsContainer>
            )}
          </SelectedAssetsTitleContainer>

          {/* Summary section */}
          <SummaryContainer>
            <span style={{ fontWeight: 'bold', marginRight: '10px' }}>
              Total: {selectedConsum?.reduce((sum, item) => sum + parseInt(item.quantity || 0, 10), 0)} items
            </span>
            {selectedConsumSummary.map((item, index) => (
              <SummaryItem key={index}>
                <SummaryItemName>{item.name}</SummaryItemName>
                <SummaryItemCount>{item.count}</SummaryItemCount>
              </SummaryItem>
            ))}
          </SummaryContainer>
          
          <StyledTableContainer component={Paper}>
            <Table>
              <StyledTableHead>
                <TableRow>
                  <TableCell>Sl.No.</TableCell>
                  <TableCell>Consumable</TableCell>
                  <TableCell>Asset</TableCell>
                  <TableCell>Asset Tag</TableCell>
                  <TableCell>Part No</TableCell>
                  <TableCell>Requested Qty</TableCell>
                </TableRow>
              </StyledTableHead>
              <TableBody>
                {selectedConsum.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{item?.consumable || '-'}</TableCell>
                    <TableCell>{item?.asset || 'Not Applicable'}</TableCell>
                    <TableCell>{item?.assettag?.label || 'Not Applicable'}</TableCell>
                    <TableCell>{item?.partno || '-'}</TableCell>
                    <TableCell>{item?.quantity || '-'}</TableCell>                    
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </StyledTableContainer>
        </SelectedAssetsContainer>
      )}
      
      {/* Action buttons */}
      {row?.curr_sent_to_tag === "storel2_bts_forward_bo" && (
        <div style={{ marginTop: '20px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
          <AcceptButton 
            onClick={handleAcceptConsumables} 
            disabled={isUpdatingReq || !latestConsumables.length}
          >
            Accept & Close Request
          </AcceptButton>

          <RejectButton 
            onClick={handleRejectRequest} 
            disabled={isUpdatingReq}
          >
            Reject Request
          </RejectButton>
        </div>
      )}

      {row?.curr_sent_to_tag === "boreq_bts_rtrned_uhlvl" && (
        <ReSubmitButton 
          onClick={handleResubmitUnitHead} 
          disabled={isUpdatingReq || !hasValidQuantity(selectedConsum)}
        >
          Resubmit to Unit Head
        </ReSubmitButton>
      )}
      
      {/* Modal for Create Issue */}
      <Modal
        open={openIssueModal}
        onClose={handleCloseIssueModal}
        aria-labelledby="issue-modal-title"
      >
        <Box sx={ModalStyle}>
          <ModalTitle id="issue-modal-title">Edit Consumables Request</ModalTitle>  

          <UnivConsumSelector
            addSpecIndex={addSpecIndex}
            setAddSpecIndex={setAddSpecIndex}
            selectedConsum={selectedConsum}
            setSelectedConsum={setSelectedConsum}
            storeSelectedValue={row.req_store}
            hidePartNumber={true}
          />        
          
          <ModalFooter>
            <CancelButton onClick={handleCloseIssueModal}>
              Cancel
            </CancelButton>
            <ClearButton onClick={handleClearAllSelections}>
              Clear All
            </ClearButton>
            <ActionButton onClick={handleConfirmSelection}>
              Confirm Selection
            </ActionButton>
          </ModalFooter>
        </Box>
      </Modal>

    </>
  );
};

export default React.memo(BoInboxFormBTS); 